import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  doc,
  runTransaction,
  serverTimestamp,
  query,
  orderBy,
  where,
  limit,
} from "firebase/firestore";
import { FaBell } from "react-icons/fa";
import "../styles/View.css";

// Paste your deployed Worker URL here (shown after `wrangler deploy`)
const EMAIL_WORKER_URL = "https://security-qr-email.sarachandrahotels.workers.dev/";

const QUOTA_MESSAGE =
  "Quota exceeded. The Firestore daily usage limit has been reached, so the email could not be checked or sent. Try again after the quota resets.";
const isQuotaError = (error) =>
  error?.code === "resource-exhausted" || /quota|resource-exhausted/i.test(error?.message || "");

// How many of the newest reviews to load for the "Recently Submitted" table and the per-user tabs.
// Lower = fewer Firestore reads. The tabs show at most 45 per user anyway.
const RECENT_REVIEWS_LIMIT = 300;

// Start/end of a Sri Lanka calendar day (UTC+05:30, no daylight saving) as JS Dates.
const colomboDayRange = (dateString) => {
  const start = new Date(`${dateString}T00:00:00+05:30`);
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  return { start, end };
};

function Viewsp() {
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]); // ✅ add state for users
  const [selectedUser, setSelectedUser] = useState(null);
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [locations, setLocations] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationDate, setNotificationDate] = useState(() => {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Colombo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const dateParts = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
    return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
  });
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState("");
  const [dayReviews, setDayReviews] = useState([]); // reviews of the selected date only
  const [dayLoadedFor, setDayLoadedFor] = useState(""); // which date dayReviews belongs to
  const [baseLoaded, setBaseLoaded] = useState(false);
  const autoSendTried = useRef(false);
  const dataLoaded = baseLoaded && dayLoadedFor === notificationDate;

  const reportLoadError = (error) => {
    console.error("Failed to load data", error);
    setEmailStatus(
      isQuotaError(error)
        ? QUOTA_MESSAGE
        : "Could not load data, so the automatic email was skipped."
    );
  };

  // One-time load: newest reviews (limited), security users only, security points only.
  useEffect(() => {
    async function fetchReviews() {
      const reviewsQuery = query(
        collection(db, "reviews"),
        orderBy("createdAt", "desc"),
        limit(RECENT_REVIEWS_LIMIT)
      );
      const querySnapshot = await getDocs(reviewsQuery);
      setReviews(querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    }

    async function fetchUsers() {
      const usersQuery = query(
        collection(db, "users"),
        where("role", "in", ["security", "Security"])
      );
      const usersSnapshot = await getDocs(usersQuery);
      setUsers(usersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    }

    async function fetchLocations() {
      const locationsQuery = query(
        collection(db, "locations"),
        where("isSecurityPoint", "==", true)
      );
      const locationsSnapshot = await getDocs(locationsQuery);
      setLocations(locationsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    }

    Promise.all([fetchReviews(), fetchUsers(), fetchLocations()])
      .then(() => setBaseLoaded(true))
      .catch(reportLoadError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reviews of the selected date only (used to work out which security points were scanned).
  useEffect(() => {
    let cancelled = false;
    const { start, end } = colomboDayRange(notificationDate);
    const dayQuery = query(
      collection(db, "reviews"),
      where("createdAt", ">=", start),
      where("createdAt", "<", end)
    );
    getDocs(dayQuery)
      .then((snapshot) => {
        if (cancelled) return;
        setDayReviews(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setDayLoadedFor(notificationDate);
      })
      .catch((error) => {
        if (!cancelled) reportLoadError(error);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationDate]);

  // Show "Email already sent" under the button whenever the selected date was already emailed.
  useEffect(() => {
    let cancelled = false;
    setEmailStatus("");
    getDoc(doc(db, "emailLogs", notificationDate))
      .then((snap) => {
        if (!cancelled && snap.exists() && snap.data().status === "sent") {
          setEmailStatus(`Email already sent for ${notificationDate}.`);
        }
      })
      .catch((error) => {
        console.error("Could not read email log", error);
        if (!cancelled && isQuotaError(error)) setEmailStatus(QUOTA_MESSAGE);
      });
    return () => {
      cancelled = true;
    };
  }, [notificationDate]);

  // mode "auto": runs on page load, at most once per date across all sessions/users.
  // mode "manual": button click, always sends (acts as a resend).
  const sendDailyDigest = async (mode = "manual") => {
    const isAuto = mode === "auto";
    const logRef = doc(db, "emailLogs", notificationDate);
    let claimed = false;
    setEmailSending(true);
    if (!isAuto) setEmailStatus("");
    try {
      await auth.authStateReady();
      if (isAuto && !auth.currentUser) return; // not signed in yet; skip silently

      if (isAuto) {
        // Atomically claim today's send so two tabs/users can't both email.
        const outcome = await runTransaction(db, async (tx) => {
          const snap = await tx.get(logRef);
          if (snap.exists()) {
            const data = snap.data();
            const claimedMs = data.claimedAt?.toMillis?.() || 0;
            const staleClaim = data.status === "sending" && Date.now() - claimedMs > 5 * 60 * 1000;
            if (!staleClaim) return data.status === "sent" ? "sent" : "busy";
          }
          tx.set(logRef, { status: "sending", claimedAt: serverTimestamp() });
          return "claimed";
        });
        if (outcome === "sent") {
          setEmailStatus(`Email already sent for ${notificationDate}.`);
          return;
        }
        if (outcome === "busy") return;
        claimed = true;
      }

      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Please sign in again before sending the email.");
      const idToken = await currentUser.getIdToken();
      const response = await fetch(EMAIL_WORKER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          date: notificationDate,
          points: Object.entries(companyNotifications).flatMap(([company, locs]) =>
            locs.map((l) => ({
              company,
              location: l.location || "",
              qrValue: l.qrValue || l.location || "",
            }))
          ),
        }),
      });
      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch {
        throw new Error(
          response.status === 405
            ? "Email worker URL is wrong or not deployed. Check EMAIL_WORKER_URL."
            :`Email endpoint returned an unexpected response (${response.status}).`
        );
      }
      if (!response.ok) throw new Error(result.error || "Email request failed.");
      if (result.status === "sent") {
        try {
          await setDoc(logRef, {
            status: "sent",
            total: result.total,
            sentAt: serverTimestamp(),
            sentBy: currentUser.email || "",
            mode,
          });
          claimed = false; // claim is now the permanent "sent" record
          setEmailStatus(`Email already sent for ${result.date} (${result.total} unscanned point(s)).`);
        } catch (logError) {
          claimed = false;
          setEmailStatus(
            isQuotaError(logError)
              ? `Email sent for ${result.date}, but it could not be recorded. ${QUOTA_MESSAGE}`
              : `Email sent for ${result.date}, but it could not be recorded.`
          );
        }
      } else {
        // Nothing to send (all points scanned): release the claim so it can run again later.
        if (claimed) await deleteDoc(logRef).catch(() => {});
        claimed = false;
        setEmailStatus(result.message || "No email sent.");
      }
    } catch (error) {
      if (claimed) await deleteDoc(logRef).catch(() => {}); // allow a retry
      setEmailStatus(isQuotaError(error) ? QUOTA_MESSAGE : error.message || "Could not send the email.");
    } finally {
      setEmailSending(false);
    }
  };

  // Automatic send: once, after all data has loaded, for today's date.
  useEffect(() => {
    if (!dataLoaded || autoSendTried.current) return;
    autoSendTried.current = true;
    sendDailyDigest("auto");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded]);


    const groupedData = reviews.reduce((acc, review) => {
      const hotel = review.hotelName || "Unknown Hotel";
      const email = review.userEmail || "Unknown Email";

      if (!acc[hotel]) acc[hotel] = {};
      if (!acc[hotel][email]) acc[hotel][email] = [];

      acc[hotel][email].push(review);
      return acc;
    }, {});

    // Sort & limit to 30 reviews per email
    Object.keys(groupedData).forEach((hotel) => {
      Object.keys(groupedData[hotel]).forEach((email) => {
        groupedData[hotel][email] = groupedData[hotel][email]
          .sort((a, b) => b.createdAt?.toMillis?.() - a.createdAt?.toMillis?.()) // newest first
          .slice(0, 45); // limit 30 per email
      });
    });

    // ✅ Top 10 recent records
    const recent10 = [...reviews]
      .sort((a, b) => b.createdAt?.toMillis?.() - a.createdAt?.toMillis?.())
      .filter((review) => {
        const user = users.find((u) => u.email === review.userEmail);
        return user?.role?.toLowerCase() === "security";
      })
      .slice(0, 10);

    const securityEmails = new Set(
      users
        .filter((user) => user.role?.toLowerCase() === "security")
        .map((user) => user.email)
    );
    const scannedSecurityQrValues = new Set(
      dayReviews
        .filter((review) => {
          if (!securityEmails.has(review.userEmail) || !review.qrData || !review.createdAt?.toDate) {
            return false;
          }
          const reviewDate = new Intl.DateTimeFormat("en-CA", {
            timeZone: "Asia/Colombo",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(review.createdAt.toDate());
          return reviewDate === notificationDate;
        })
        .map((review) => `${String(review.hotelName || "").trim().toLowerCase()}::${String(review.qrData).trim().toLowerCase()}`)
    );
    const companyNotifications = locations
      .filter((location) =>
        location.isSecurityPoint === true &&
        !scannedSecurityQrValues.has(`${String(location.company || "").trim().toLowerCase()}::${String(location.qrValue || location.location || "").trim().toLowerCase()}`)
      )
      .reduce((groups, location) => {
        const company = location.company || "Company not set";
        if (!groups[company]) groups[company] = [];
        groups[company].push(location);
        return groups;
      }, {});

    const notificationCount = Object.values(companyNotifications)
      .reduce((count, companyLocations) => count + companyLocations.length, 0);


  return (
    <div className="admin-container">
      <div className="viewsp-notification-widget">
        <button
          className="viewsp-notification-button"
          type="button"
          aria-label={`Notifications: ${notificationCount} unscanned security QR points`}
          aria-expanded={notificationsOpen}
          onClick={() => setNotificationsOpen((open) => !open)}
        >
          <FaBell aria-hidden="true" />
          {notificationCount > 0 && <span className="viewsp-notification-count">{notificationCount}</span>}
        </button>

        {notificationsOpen && (
          <section className="viewsp-notification-panel" aria-label="Company notifications">
            <div className="viewsp-notification-heading">
              <h3>Daily Security QR Status</h3>
              <button type="button" onClick={() => setNotificationsOpen(false)} aria-label="Close notifications">
                ×
              </button>
            </div>
            <label className="viewsp-notification-date">
              Date
              <input
                type="date"
                value={notificationDate}
                max={new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Colombo" }).format(new Date())}
                onChange={(event) => setNotificationDate(event.target.value)}
              />
            </label>
            <button
              className="viewsp-send-digest-button"
              type="button"
              onClick={() => sendDailyDigest("manual")}
              disabled={emailSending}
            >
              {emailSending ? "Sending…" : `Email ${notificationDate} Summary`}
            </button>
            {emailStatus && <p className="viewsp-email-status" role="status">{emailStatus}</p>}
            {notificationCount === 0 ? (
              <p className="viewsp-no-notifications">All marked security QR points were scanned on this date.</p>
            ) : (
              Object.entries(companyNotifications).map(([company, companyLocations]) => (
                <details className="viewsp-notification-company" key={company} open>
                  <summary>{company}<span>{companyLocations.length}</span></summary>
                  {companyLocations.map((location) => {
                    return (
                      <article className="viewsp-notification-item" key={location.id}>
                        <strong>{location.location || "Unnamed security point"}</strong>
                        <span>QR: {location.qrValue || location.location || "QR value unavailable"}</span>
                        <span>Not scanned on {notificationDate}</span>
                      </article>
                    );
                  })}
                </details>
              ))
            )}
          </section>
        )}
      </div>
      <h2 style={{color:"black"}}>Latest Security Reviews</h2>

      <button className="home-button" onClick={() => navigate("/superadmin")}>
            Back
       </button>

      
       {/* ✅ Show top 10 recent records */}
      <div className="recent-section">
        <h3>Recently Submitted (Last 10)</h3>
        <div className="table1">
          <table className="review-table">
            <thead>
              <tr>
                {/* <th>NO</th> */}
                <th>Date</th>
                <th>QR Data</th>
                <th>User</th>               
                {/* <th>Rating</th> */}
                <th>Hotel Issues</th>
                <th>Maintenance Issues</th>
                <th>Image</th>
                {/* <th>User IP</th> */}
                <th>Location</th>
                
              </tr>
            </thead>
            <tbody>
              {recent10.map((review, index) => {
                 const reviewDate = review.createdAt?.toDate ? review.createdAt.toDate() : null;

                  // Check if the review was submitted today
                  const isToday =
                    reviewDate &&
                    reviewDate.toDateString() === new Date().toDateString();

                  // ✅ Format date as DD/MM/YYYY and time as HH:mm:ss (24h)
                  const formattedDateTime = reviewDate
                  ? `${reviewDate.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })} ${reviewDate.toLocaleTimeString("en-GB", {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}`
                  : "";

                  return (
                    <tr
                      key={review.id}
                      className={`
                       
                        ${isToday ? "today-review" : "older-review"}
                      `}
                      >
                    {/* <td>{index + 1}</td> */}
                    <td>{formattedDateTime}</td>
                    <td>{review.qrData || "N/A"}</td>
                    <td>{review.userEmail ? review.userEmail.split('@')[0] : "Unknown"}</td>                   
                    {/* <td>{review.rating}/5</td> */}
                    <td>{review.issueHotel}</td>
                    <td>{review.issueMaintenance}</td>
                     <td>
                        {review.imageUrls && review.imageUrls.length > 0 ? (
                        <div className="thumbnail-row">
                          {review.imageUrls.map((url, idx) => (
                            <img
                              key={idx}
                              src={url}
                              alt={`Uploaded ${idx + 1}`}
                              className="review-thumbnail"
                              onClick={() => setSelectedImage(url)} // open popup
                            />
                          ))}
                        </div>
                      ) : (
                        "No image"
                      )}
                                </td>
                    {/* <td>{review.userIp || "Unknown"}</td> */}
                    <td>
                      {review.location?.lat && review.location?.lng ? (
                        <a
                          href={`https://www.google.com/maps?q=${review.location.lat},${review.location.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Location
                        </a>
                      ) : (
                        "No Location"
                      )}
                    </td>
                    
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div><br></br>

     {Object.keys(groupedData).map((hotel) => (
        <div key={hotel} className="hotel-section">
            <h3>{hotel}</h3>

            {/* User Tabs - only role=security */}
            <div className="table1">
            <div className="tab-container">
                {Object.keys(groupedData[hotel])
                .filter((email) => {
                    const user = users.find((u) => u.email === email);
                    return user?.role === "security"; // ✅ only show security
                })
                .map((email) => (
                    <button
                    key={email}
                    className={`tab-btn ${selectedUser === email ? "active selected-email" : ""}`}
                    onClick={() => setSelectedUser(email)}
                    >
                    {email} (Security)
                    </button>
                ))}
            </div>


        {/* Show table only if role is security */} 
          {selectedUser && groupedData[hotel][selectedUser] && (() => {
            const user = users.find((u) => u.email === selectedUser);
            if (user?.role !== "security") return null; // ✅ block non-security just in case


            return user?.role === "security" ? (
            <table className="review-table">
              <thead>
                <tr>
                   <th>Date</th>
                  {/* <th>NO</th> */}
                  <th>QR Data</th>
                  {/* <th>Rating</th> */}
                  <th>Hotel Issues</th>
                  <th>Maintenance Issues</th>
                  <th>Image</th>
                  {/* <th>User IP</th> */}
                  <th>Location</th>
                 
                </tr>
              </thead>
              <tbody>
                {groupedData[hotel][selectedUser].map((review, index) => {
                  // Convert Firestore timestamp to JS Date
                  const reviewDate = review.createdAt?.toDate ? review.createdAt.toDate() : null;

                  // Check if the review was submitted today
                  const isToday =
                    reviewDate &&
                    reviewDate.toDateString() === new Date().toDateString();

                 const formattedDateTime = reviewDate
                    ? `${reviewDate.toLocaleDateString("en-GB")} ${reviewDate.toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}`
                    : "";

                  return (
                    <tr
                      key={review.id}
                      className={`
                       
                        ${isToday ? "today-review" : "older-review"}
                      `}
                      >

                      {/* New Number Column */}
                      {/* <td>{index + 1}</td> */}
                      <td>{formattedDateTime}</td>
                      <td>{review.qrData || "N/A"}</td>
                      {/* <td>{review.rating}/5</td> */}
                      <td>{review.issueHotel}</td>
                      <td>{review.issueMaintenance}</td>
                       <td>
                           {review.imageUrls && review.imageUrls.length > 0 ? (
                        <div className="thumbnail-row">
                          {review.imageUrls.map((url, idx) => (
                            <img
                              key={idx}
                              src={url}
                              alt={`Uploaded ${idx + 1}`}
                              className="review-thumbnail"
                              onClick={() => setSelectedImage(url)} // open popup
                            />
                          ))}
                        </div>
                      ) : (
                        "No image"
                      )}
                        </td>
                      {/* <td>{review.userIp || "Unknown"}</td> */}
                      <td>
                        {review.location && review.location.lat && review.location.lng ? (
                          <a
                            href={`https://www.google.com/maps?q=${review.location.lat},${review.location.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Location
                          </a>
                        ) : (
                          "No Location"
                        )}
                      </td>
                     
                    </tr>
                  );
                })}
              </tbody>

            </table>
             ) : null;
            
          })()}

          {selectedImage && (
            <div className="image-modal" onClick={() => setSelectedImage(null)}>
              <span className="close-btn">&times;</span>
              <img src={selectedImage} alt="Full view" className="modal-content" />
            </div>
          )}
        </div>
      </div>
      ))}
    </div>
  );
}

export default Viewsp;  